import { Controller } from "@hotwired/stimulus";
import { formatNumberDisplay } from "./utils/number_format";

/**
 * new-vs-old-chart (Stimulus controller)
 *
 * Dessine le coût cumulé de possession du bloc « Comparateur neuf / ancien »
 * (issue #650) en SVG, plutôt qu'avec Google Charts : la lecture attendue
 * demande une aire d'économie entre les deux courbes, un repère de revente, une
 * pastille d'écart et un marqueur de bascule, qu'une `LineChart` ne rend pas.
 *
 * Le contrôleur est posé sur l'event sink du bloc (`data-api-sync-event-sink`),
 * pas sur le conteneur du graphique : `remote:update:success` est émis sur le
 * sink et **remonte**, il n'atteindrait donc pas un contrôleur situé plus bas.
 *
 * Redessine sur `remote:update:success` (nouvelles données), `reveal:toggle`
 * (le bloc est replié par défaut, un SVG dessiné dans un conteneur de largeur
 * nulle sortirait vide) et au redimensionnement.
 *
 * Les deux séries prennent leurs couleurs sur les variables de thème
 * (`--color-1` pour le neuf, `--color-2` pour l'ancien, comme `text-color-1` /
 * `text-color-2` dans la vue) pour suivre le skin de la source ; le reste du
 * style est dans `_simulator_new_vs_old.scss`.
 */
const SVG_NS = "http://www.w3.org/2000/svg";

// Marges du repère de tracé. Les libellés « Neuf » / « Ancien » sont tracés à
// l'intérieur du repère, la marge droite ne sert donc qu'à éviter que le dernier
// point ne touche le bord. En haut, de la place pour « Revente à X ans ».
const PADDING = { top: 26, right: 10, bottom: 22, left: 46 };
const Y_TICKS = 4;

export default class extends Controller {
  static targets = ["canvas", "legend"];

  connect() {
    this.chartData = null;

    this.onRemoteUpdate = (event) => {
      const json = event?.detail?.json;
      if (!json || !Object.prototype.hasOwnProperty.call(json, "new_vs_old")) return;
      this.chartData = json.new_vs_old ? json.new_vs_old.chart : null;
      this.draw();
    };
    this.onToggle = () => this.draw();

    this.element.addEventListener("remote:update:success", this.onRemoteUpdate);
    this.element.addEventListener("reveal:toggle", this.onToggle);

    if (typeof ResizeObserver !== "undefined" && this.hasCanvasTarget) {
      this.observer = new ResizeObserver(() => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.draw(), 80);
      });
      this.observer.observe(this.canvasTarget);
    }
  }

  disconnect() {
    this.element.removeEventListener("remote:update:success", this.onRemoteUpdate);
    this.element.removeEventListener("reveal:toggle", this.onToggle);
    if (this.observer) this.observer.disconnect();
    clearTimeout(this.resizeTimer);
  }

  // ------------------------------------------------------------- rendu

  draw() {
    if (!this.hasCanvasTarget) return;
    const canvas = this.canvasTarget;
    const data = this.chartData;

    if (!data || !Array.isArray(data.years) || data.years.length === 0) {
      canvas.innerHTML = "";
      return;
    }
    // Conteneur replié ou masqué : un SVG dessiné ici sortirait sans dimensions.
    if (canvas.offsetWidth === 0 || canvas.offsetHeight === 0) return;

    const parts = this.ensureSkeleton(canvas);
    const box = parts.svg.getBoundingClientRect();
    const width = Math.round(box.width);
    const height = Math.round(box.height);
    if (width === 0 || height === 0) return;

    const geometry = this.computeGeometry(data, width, height);
    if (this.hasLegendTarget) this.renderLegend(this.legendTarget, geometry);

    // viewBox calé sur la taille mesurée : une unité du repère vaut un pixel
    // CSS, ce qui permet de positionner la bulle de survol sans conversion.
    parts.svg.setAttribute("viewBox", `0 0 ${width} ${height}`);
    parts.svg.innerHTML = "";

    this.renderGrid(parts.svg, geometry);
    this.renderSavingArea(parts.svg, geometry);
    this.renderSeries(parts.svg, geometry);
    this.renderResaleMarker(parts.svg, geometry);
    this.renderTippingPoint(parts.svg, geometry);
    this.renderSeriesLabels(parts.svg, geometry);

    this.bindTooltip(parts, geometry);
  }

  /**
   * SVG et bulle de survol : construits une seule fois, puis réutilisés à chaque
   * redessin. La légende, elle, vit hors du canvas (sur la ligne du titre) et
   * est fournie par la vue via le target `legend`.
   */
  ensureSkeleton(canvas) {
    if (this.skeleton && canvas.contains(this.skeleton.svg)) return this.skeleton;

    canvas.innerHTML = "";

    const svg = document.createElementNS(SVG_NS, "svg");
    svg.setAttribute("preserveAspectRatio", "none");

    const tooltip = document.createElement("div");
    tooltip.className = "simulator-new-vs-old-chart__tooltip";
    tooltip.hidden = true;

    canvas.appendChild(svg);
    canvas.appendChild(tooltip);

    this.skeleton = { svg, tooltip };
    return this.skeleton;
  }

  computeGeometry(data, width, height) {
    const years = data.years.map((year) => Number(year));
    const newSeries = years.map((_, i) => Number(data.new[i]) || 0);
    const oldSeries = years.map((_, i) => Number(data.old[i]) || 0);

    const max = this.niceMax(Math.max(...newSeries, ...oldSeries, 1), Y_TICKS);
    const plotWidth = Math.max(width - PADDING.left - PADDING.right, 1);
    const plotHeight = Math.max(height - PADDING.top - PADDING.bottom, 1);
    const step = years.length > 1 ? plotWidth / (years.length - 1) : 0;

    const x = (index) => PADDING.left + index * step;
    const y = (value) => PADDING.top + plotHeight - (value / max) * plotHeight;

    return {
      data,
      years,
      newSeries,
      oldSeries,
      max,
      x,
      y,
      step,
      plotWidth,
      plotHeight,
      newColor: this.themeColor("--color-1", "#ff7e34"),
      oldColor: this.themeColor("--color-2", "#0f766e"),
      crossing: this.computeCrossing(newSeries, oldSeries, x, y)
    };
  }

  /**
   * Plafond de l'axe des ordonnées arrondi au pas « lisible » immédiatement
   * supérieur, pour que les graduations tombent sur des montants ronds plutôt
   * que sur le maximum brut des séries.
   */
  niceMax(value, ticks) {
    const rough = value / ticks;
    const magnitude = 10 ** Math.floor(Math.log10(rough));
    const normalized = rough / magnitude;
    const step = [1, 2, 2.5, 5, 10].find((candidate) => normalized <= candidate) * magnitude;

    return step * ticks;
  }

  /**
   * Point de croisement des deux courbes, interpolé linéairement entre les deux
   * années qui encadrent le changement de signe de l'écart. `null` si l'ancien
   * ne devient jamais plus coûteux sur l'horizon.
   */
  computeCrossing(newSeries, oldSeries, x, y) {
    const diff = (i) => oldSeries[i] - newSeries[i];
    const index = oldSeries.findIndex((_, i) => diff(i) > 0);
    if (index === -1) return null;
    if (index === 0) return { index: 0, x: x(0), y: y(newSeries[0]) };

    const previous = diff(index - 1);
    const current = diff(index);
    const ratio = previous === current ? 0 : previous / (previous - current);

    return {
      index,
      ratio,
      x: x(index - 1) + ratio * (x(index) - x(index - 1)),
      y: y(newSeries[index - 1] + ratio * (newSeries[index] - newSeries[index - 1]))
    };
  }

  themeColor(variable, fallback) {
    try {
      const value = getComputedStyle(this.element).getPropertyValue(variable).trim();
      return value || fallback;
    } catch (_) {
      return fallback;
    }
  }

  // ------------------------------------------------------- sous-rendus

  renderLegend(legend, geo) {
    const data = geo.data;
    legend.innerHTML = "";
    const items = [
      { label: data.new_label || "Neuf", color: geo.newColor },
      { label: data.old_label || "Ancien", color: geo.oldColor }
    ];
    // L'aire n'est tracée qu'à partir du point de bascule : pas de bascule sur
    // l'horizon, pas d'aire, donc pas d'entrée de légende.
    if (geo.crossing) {
      items.push({
        label: data.saving_label || "Économie avec le neuf",
        color: geo.oldColor,
        area: true
      });
    }

    items.forEach((item) => {
      const wrapper = document.createElement("span");
      wrapper.className = "simulator-new-vs-old-chart__legend-item";
      const swatch = document.createElement("span");
      swatch.className = `simulator-new-vs-old-chart__legend-swatch${item.area ? " is-area" : ""}`;
      swatch.style.background = item.color;
      wrapper.appendChild(swatch);
      wrapper.appendChild(document.createTextNode(item.label));
      legend.appendChild(wrapper);
    });
  }

  renderGrid(svg, geo) {
    for (let tick = 0; tick <= Y_TICKS; tick += 1) {
      const value = (geo.max / Y_TICKS) * tick;
      const yPos = geo.y(value);
      svg.appendChild(
        this.svgNode("line", {
          class: "simulator-new-vs-old-chart__grid-line",
          x1: PADDING.left,
          x2: PADDING.left + geo.plotWidth,
          y1: yPos,
          y2: yPos
        })
      );
      svg.appendChild(
        this.svgText(this.shortCurrency(value), {
          class: "simulator-new-vs-old-chart__axis-label",
          x: PADDING.left - 6,
          y: yPos + 3,
          "text-anchor": "end"
        })
      );
    }

    const baseline = geo.y(0);
    geo.years.forEach((year, index) => {
      svg.appendChild(
        this.svgText(String(year), {
          class: "simulator-new-vs-old-chart__axis-label",
          x: geo.x(index),
          y: baseline + 14,
          "text-anchor": "middle"
        })
      );
    });
  }

  /**
   * Aire entre les deux courbes à partir du point de bascule : c'est l'économie
   * réalisée en choisissant le neuf.
   */
  renderSavingArea(svg, geo) {
    const crossing = geo.crossing;
    if (!crossing) return;

    const from = crossing.index;
    const upper = [];
    const lower = [];
    for (let i = from; i < geo.years.length; i += 1) {
      upper.push(`${geo.x(i)},${geo.y(geo.oldSeries[i])}`);
      lower.push(`${geo.x(i)},${geo.y(geo.newSeries[i])}`);
    }
    const points = [`${crossing.x},${crossing.y}`, ...upper, ...lower.reverse()].join(" ");

    svg.appendChild(
      this.svgNode("polygon", {
        class: "simulator-new-vs-old-chart__saving-area",
        points,
        fill: geo.oldColor
      })
    );
  }

  renderSeries(svg, geo) {
    [
      { values: geo.newSeries, color: geo.newColor },
      { values: geo.oldSeries, color: geo.oldColor }
    ].forEach(({ values, color }) => {
      svg.appendChild(
        this.svgNode("polyline", {
          class: "simulator-new-vs-old-chart__series",
          points: values.map((value, i) => `${geo.x(i)},${geo.y(value)}`).join(" "),
          stroke: color
        })
      );
    });
  }

  renderResaleMarker(svg, geo) {
    const index = geo.years.indexOf(Number(geo.data.resale_year));
    if (index === -1) return;

    const xPos = geo.x(index);
    svg.appendChild(
      this.svgNode("line", {
        class: "simulator-new-vs-old-chart__resale-line",
        x1: xPos,
        x2: xPos,
        y1: PADDING.top - 8,
        y2: geo.y(0)
      })
    );
    svg.appendChild(
      this.svgText(geo.data.resale_label || "Revente", {
        class: "simulator-new-vs-old-chart__resale-label",
        x: xPos,
        y: PADDING.top - 13,
        "text-anchor": this.anchorFor(xPos, geo)
      })
    );

    this.renderDifferenceBadge(svg, geo, index, xPos);
  }

  /**
   * Pastille d'écart au niveau du repère de revente : verte quand le neuf est le
   * moins cher, ambre sinon.
   */
  renderDifferenceBadge(svg, geo, index, xPos) {
    const difference = Number(geo.data.difference_at_resale);
    if (!Number.isFinite(difference)) return;

    // Écart positif = le neuf coûte moins cher, d'où le signe « moins » devant
    // la valeur absolue (et la pastille verte).
    const positive = difference >= 0;
    const label = `${positive ? "\u2212" : "+"}\u00A0${this.currency(Math.abs(difference))}`;
    const middle = (geo.y(geo.newSeries[index]) + geo.y(geo.oldSeries[index])) / 2;
    const boxWidth = Math.max(label.length * 6.2 + 12, 56);
    const boxHeight = 18;
    const boxX = this.clampBadgeX(xPos, boxWidth, geo);

    svg.appendChild(
      this.svgNode("rect", {
        class: `simulator-new-vs-old-chart__badge ${positive ? "is-positive" : "is-negative"}`,
        x: boxX,
        y: middle - boxHeight / 2,
        width: boxWidth,
        height: boxHeight,
        rx: 9
      })
    );
    svg.appendChild(
      this.svgText(label, {
        class: "simulator-new-vs-old-chart__badge-text",
        x: boxX + boxWidth / 2,
        y: middle + 4,
        "text-anchor": "middle"
      })
    );
  }

  renderTippingPoint(svg, geo) {
    const crossing = geo.crossing;
    if (!crossing) return;

    svg.appendChild(
      this.svgNode("circle", {
        class: "simulator-new-vs-old-chart__tipping-marker",
        cx: crossing.x,
        cy: crossing.y,
        r: 4
      })
    );
    // Au-dessus du marqueur : une bascule précoce est proche de la ligne de base,
    // où le libellé viendrait recouvrir les années de l'axe des abscisses.
    svg.appendChild(
      this.svgText(geo.data.tipping_point_label || "Bascule", {
        class: "simulator-new-vs-old-chart__tipping-label",
        x: crossing.x,
        y: crossing.y - 10,
        "text-anchor": this.anchorFor(crossing.x, geo)
      })
    );
  }

  /**
   * Libellés en bout de courbe, tracés **dans** le repère et décalés
   * verticalement de part et d'autre : « Ancien » au-dessus de sa courbe,
   * « Neuf » sous la sienne. Les sortir à droite imposerait une marge morte
   * prise sur la largeur du tracé.
   */
  renderSeriesLabels(svg, geo) {
    const last = geo.years.length - 1;
    [
      { label: geo.data.old_label || "Ancien", value: geo.oldSeries[last], color: geo.oldColor, offset: -8 },
      { label: geo.data.new_label || "Neuf", value: geo.newSeries[last], color: geo.newColor, offset: 15 }
    ].forEach(({ label, value, color, offset }) => {
      svg.appendChild(
        this.svgText(label, {
          class: "simulator-new-vs-old-chart__series-label",
          x: geo.x(last),
          y: geo.y(value) + offset,
          "text-anchor": "end",
          fill: color
        })
      );
    });
  }

  // ---------------------------------------------------------- tooltip

  bindTooltip(parts, geo) {
    const { svg, tooltip } = parts;

    if (this.tooltipHandlers) {
      svg.removeEventListener("mousemove", this.tooltipHandlers.move);
      svg.removeEventListener("mouseleave", this.tooltipHandlers.leave);
    }

    // Le viewBox est calé sur la taille mesurée du SVG, et le SVG occupe tout le
    // canvas : les coordonnées du repère sont directement des pixels relatifs au
    // conteneur positionné, sans décalage à appliquer.
    const move = (event) => {
      const rect = svg.getBoundingClientRect();
      if (rect.width === 0) return;
      const index = this.nearestIndex(event.clientX - rect.left, geo);
      if (index == null) return;

      const difference = geo.oldSeries[index] - geo.newSeries[index];
      tooltip.innerHTML =
        `<b>Année ${geo.years[index]}</b><br>` +
        `${geo.data.new_label || "Neuf"} : ${this.currency(geo.newSeries[index])}<br>` +
        `${geo.data.old_label || "Ancien"} : ${this.currency(geo.oldSeries[index])}<br>` +
        `Écart : <b>${this.currency(difference)}</b>`;
      tooltip.hidden = false;
      tooltip.style.left = `${geo.x(index)}px`;
      tooltip.style.top = `${geo.y(Math.min(geo.newSeries[index], geo.oldSeries[index])) - 8}px`;
    };
    const leave = () => {
      tooltip.hidden = true;
    };

    svg.addEventListener("mousemove", move);
    svg.addEventListener("mouseleave", leave);
    this.tooltipHandlers = { move, leave };
  }

  nearestIndex(svgX, geo) {
    if (geo.step === 0) return 0;
    const index = Math.round((svgX - PADDING.left) / geo.step);
    if (index < 0 || index >= geo.years.length) return null;
    return index;
  }

  // ---------------------------------------------------------- helpers

  anchorFor(xPos, geo) {
    if (xPos < PADDING.left + 40) return "start";
    if (xPos > PADDING.left + geo.plotWidth - 40) return "end";
    return "middle";
  }

  clampBadgeX(xPos, boxWidth, geo) {
    const left = xPos + 8;
    const maxLeft = PADDING.left + geo.plotWidth - boxWidth;
    return Math.min(Math.max(left, PADDING.left), Math.max(maxLeft, PADDING.left));
  }

  currency(value) {
    return formatNumberDisplay(value, { decimals: 0, unit: "€" });
  }

  /**
   * Graduations de l'axe des ordonnées en milliers d'euros : sur une échelle à
   * six chiffres, les montants complets mangent la largeur du tracé. Le reste du
   * graphique (bulle de survol, pastille d'écart) garde le format complet.
   */
  shortCurrency(value) {
    if (value === 0) return "0 €";

    return `${formatNumberDisplay(value / 1000, { decimals: 0 })} k€`;
  }

  svgNode(name, attributes) {
    const node = document.createElementNS(SVG_NS, name);
    Object.entries(attributes).forEach(([key, value]) => {
      node.setAttribute(key, String(value));
    });
    return node;
  }

  svgText(text, attributes) {
    const node = this.svgNode("text", attributes);
    node.textContent = text;
    return node;
  }
}
